import { alpha, type Theme } from "@mui/material";
import { COR_FERIADO } from "@/components/escala/tiposDia";
import type { DiaColuna } from "@/components/escala/espelho";

export const LARGURA_COLABORADOR = 248;
// Até onde o espelho deixa arrastar a coluna do nome.
export const LARGURA_COLABORADOR_MIN = 200;
export const LARGURA_COLABORADOR_MAX = 600;
// A largura da coluna do nome é uma variável CSS da tabela: arrastar a borda muda só ela, sem
// redesenhar as linhas. Onde ninguém a define (os relatórios), vale LARGURA_COLABORADOR.
export const VAR_LARGURA_COLABORADOR = "--largura-colaborador";
// A largura da coluna, menos o que a célula gasta fora do texto (avatar, botões, padding).
export const larguraColaborador = (desconto = 0) =>
  `calc(var(${VAR_LARGURA_COLABORADOR}, ${LARGURA_COLABORADOR}px) - ${desconto}px)`;
export const LARGURA_DIA = 48;
export const LARGURA_TOTAL = 52;
export const ALTURA_LINHA = 42;
export const ALTURA_CABECALHO = 52;
export const ALTURA_TOTAIS = 34;

export const celulaBase = {
  p: 0,
  height: ALTURA_LINHA,
  textAlign: "center",
  borderBottom: "1px solid",
  borderColor: "divider",
} as const;

export const rotuloCaixaAlta = {
  color: "text.secondary",
  fontWeight: 700,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
} as const;

export const fixaEsquerda = {
  position: "sticky",
  left: 0,
  zIndex: 2,
  bgcolor: "background.paper",
  borderRight: "1px solid",
  borderRightColor: "divider",
} as const;

export const fixaDireita = (right: number) =>
  ({ position: "sticky", right, zIndex: 2, bgcolor: "background.paper" }) as const;

// Domingo e feriado ganham faixa na coluna inteira. Vai como gradiente por
// cima do bgcolor, não no lugar dele: as células sticky precisam continuar
// opacas, senão a grade rolando transparece.
export const faixa = (c: Pick<DiaColuna, "domingo" | "feriado">) => (theme: Theme) => {
  if (!c.domingo && !c.feriado) return {};
  const cor = c.feriado ? alpha(COR_FERIADO, 0.22) : alpha(theme.palette.text.primary, 0.055);
  return { backgroundImage: `linear-gradient(${cor}, ${cor})` };
};

// "?" (T sem horário): contorno tracejado na cor da letra.
export const contornoSemHorario = (cor: string) =>
  ({ border: "1.5px dashed", borderColor: cor, boxSizing: "border-box" }) as const;

// Dia alterado à mão: o canto superior direito fica reto e é fechado por um triângulo pequeno.
export const cantoAlterado = { position: "relative", borderTopRightRadius: 0 } as const;

// Preto com contorno branco, igual em todo tipo de dia, nos dois temas e na impressão, colorida ou
// em preto e branco. O cinza claro de antes quase sumia; amarelo, rosa e laranja se confundem com
// algum tipo (A, F, FA) ou com a cor das ocorrências. O contorno branco separa o preto dos fundos
// escuros (o D e o tema escuro).
export const COR_MARCA_ALTERADO = "#111111";

export const trianguloAlterado = (cor: string = COR_MARCA_ALTERADO) =>
  ({
    position: "absolute",
    top: 0,
    right: 0,
    width: 0,
    height: 0,
    borderTop: `9px solid ${cor}`,
    borderLeft: "9px solid transparent",
    filter: "drop-shadow(0 0 1px #fff) drop-shadow(0 0 0.5px #fff)",
  }) as const;

// Botão redondo do canto inferior direito do espelho (imprimir, legenda). Fica por cima da grade,
// então o fundo é opaco em todo estado — o padrão do Fab desabilitado é translúcido.
export const botaoFlutuante = {
  bgcolor: "background.paper",
  color: "primary.main",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: 3,
  "&:hover": { bgcolor: "background.subtle" },
  "&.Mui-disabled": { bgcolor: "background.subtle", color: "text.disabled", boxShadow: 1 },
} as const;

// Colunas do mês anterior: atenuadas, com um traço separando do dia 1 na última.
export const historicoAtenuado = { opacity: 0.28 } as const;
export const separadorHistorico = { borderRight: "2px solid", borderRightColor: "divider" } as const;
